import { Flex, Group, ScrollArea, Table, Text } from '@mantine/core';
import AutomationMenu from '@/pages/Automations/components/AutomationMenu.tsx';
import TableElementWithLink from '@/components/TableElementWithLink.tsx';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import React from 'react';
import AppIcon from '@/components/AppIcon.tsx';
import {
  HiArrowPath,
  HiCalendar,
  HiOutlinePlay,
  HiOutlineRectangleGroup,
  HiOutlineRectangleStack,
  HiPower,
} from 'react-icons/hi2';
import { IconType } from 'react-icons';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import { Automation } from '@/types/automations.ts';

type AutomationsTableProps = {
  automations: Automation[];
};

const AutomationsTable = ({ automations }: AutomationsTableProps) => {
  return (
    <>
      {/*<Divider style={{ borderTop: '1px solid #C6CDD3' }} />*/}
      <ScrollArea
        scrollbars={'y'}
        offsetScrollbars={'y'}
        mr={'-12px'}
        // style={{ borderTop: '1px solid #C6CDD3' }}
      >
        <Table
          stickyHeader={true}
          verticalSpacing="7px"
          highlightOnHover
          highlightOnHoverColor={'#F5F6F8'}
          style={{ width: '100%', tableLayout: 'fixed' }}
        >
          <Table.Thead c={'#757575'}>
            <Table.Tr>
              <Table.Th p={'xs'} fw={500} style={{ width: 'auto' }}>
                <Group gap={'5px'}>
                  <Icon icon={HiOutlineRectangleGroup} />
                  Automation
                </Group>
              </Table.Th>
              <Table.Th p={'xs'} fw={500} style={{ minWidth: 60, maxWidth: 100, width: '10%' }}>
                <Group gap={'5px'}>
                  <Icon icon={HiOutlineRectangleStack} />
                  Runs
                </Group>
              </Table.Th>
              <Table.Th
                p={'xs'}
                fw={500}
                style={{ whiteSpace: 'nowrap', minWidth: 100, maxWidth: 200, width: '15%' }}
              >
                <Group gap={'5px'}>
                  {/* HiOutlinePlay, HiOutlineCog, HiOutlineCpuChip, HiOutlineBolt */}
                  <Icon icon={HiOutlinePlay} />
                  Last run
                </Group>
              </Table.Th>
              <Table.Th
                p={'xs'}
                fw={500}
                style={{ whiteSpace: 'nowrap', minWidth: 100, maxWidth: 200, width: '15%' }}
              >
                <Group gap={'5px'}>
                  <Icon icon={HiArrowPath} />
                  Last published
                </Group>
              </Table.Th>
              <Table.Th
                p={'xs'}
                fw={500}
                style={{ whiteSpace: 'nowrap', minWidth: 100, maxWidth: 200, width: '15%' }}
              >
                <Group gap={'5px'}>
                  <Icon icon={HiCalendar} />
                  Created on
                </Group>
              </Table.Th>

              <Table.Th p={'xs'} fw={500} style={{ minWidth: 60, maxWidth: 100, width: '10%' }}>
                <Group gap={'5px'}>
                  <Icon icon={HiPower} />
                  Status
                </Group>
              </Table.Th>
              <Table.Th fw={500} style={{ width: 40 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {automations.map((item) => (
              <Table.Tr key={item.id} style={{ cursor: 'pointer', opacity: 0.75 }}>
                <TableElementWithLink to={`/automations/${item.id}`}>
                  <Group
                    w="100%"
                    style={{ minWidth: 0 }}
                    wrap={'nowrap'}
                    gap={'2px'}
                    align={'flex-start'}
                    mr={'xs'}
                  >
                    <StatusIndicator status={item.latest_run_status} include_text={false} />
                    <Flex style={{ flexDirection: 'column', minWidth: 0 }}>
                      <Text c="#0F172A" fw={600} size="13px" truncate={'end'} lh="20px">
                        {item.name}
                      </Text>
                      {item.description && (
                        <Text c="#757575" size="12px" truncate={'end'} pt={'4px'}>
                          {item.description}
                        </Text>
                      )}
                    </Flex>
                  </Group>
                </TableElementWithLink>

                <TableElementWithLink
                  to={`/automations/${item.id}`}
                  // style={{ justifyItems: 'center' }}
                >
                  {item.run_count}
                </TableElementWithLink>
                <TableElementWithLink to={`/automations/${item.id}`} color={'#0F172A'}>
                  {formatCalendarStyle(item.latest_run_at)}
                </TableElementWithLink>
                <TableElementWithLink to={`/automations/${item.id}`}>
                  {formatCalendarStyle(item.published_version?.created_at)}
                </TableElementWithLink>
                <TableElementWithLink to={`/automations/${item.id}`}>
                  {formatCalendarStyle(item.created_at)}
                </TableElementWithLink>
                <TableElementWithLink
                  to={`/automations/${item.id}`}
                >
                  <Flex
                    style={{
                      borderRadius: '8px',
                      backgroundColor: item.published_version ? '#C8F2DA' : '#F8F9FA',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: item.published_version ? '#CBF7E2' : '#EEEFF1',
                      padding: '2px 8px',
                      color: item.published_version ? '#28834E' : '#4C4D50',
                      fontSize: '12px',
                    }}
                  >
                    {item.published_version ? 'Published' : 'Draft only'}
                  </Flex>
                </TableElementWithLink>
                <TableElementWithLink to={`/automations/${item.id}`}>
                  <AutomationMenu automation={item} />
                </TableElementWithLink>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
};

export default AutomationsTable;

const Icon = ({ icon }: { icon: IconType }) => {
  return <AppIcon Icon={icon} color={'#757575'} size={16} visibleFrom={'lg'} />;
};
